/**
 * Mlyn Main Collection - Consolidated Alpine.js Components
 * 
 * This file consolidates all Alpine.js components used in the collection page:
 * - mlynCollectionSearch: AJAX search functionality
 * - mlynCardAtc: Product card add-to-cart functionality
 * 
 * @requires Alpine.js
 */

document.addEventListener('alpine:init', () => {

    /**
     * Product Card Add-to-Cart Component
     * Handles variant selection and AJAX add-to-cart for product cards
     */
    Alpine.data('mlynCardAtc', ({ hasVariants, defaultVariantId, defaultVariantTitle, available }) => ({
        hasVariants,
        defaultVariantId,
        selectedTitle: defaultVariantTitle || '',
        available,
        variantDropdownOpen: false,
        loading: false,

        selectVariant(payload) {
            if (!payload) return;
            this.variantDropdownOpen = false;
            this.selectedTitle = payload.title || '';
            if (payload.available) {
                this.addToCart(payload.id);
            }
        },

        async addToCart(variantId) {
            if (!variantId || this.loading) return;
            this.loading = true;
            this.variantDropdownOpen = false;

            try {
                const sectionsToRender = Alpine.store('xCartHelper')?.getSectionsToRender()?.map(s => s.id) || [];
                const formData = {
                    items: [{ id: variantId, quantity: 1 }],
                    sections: sectionsToRender
                };

                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(formData)
                });

                const data = await response.json();

                if (data.status === 422) {
                    console.error('Add to cart error:', data.description);
                } else {
                    // Update cart sections
                    if (data.sections && Alpine.store('xCartHelper')) {
                        Alpine.store('xCartHelper').getSectionsToRender().forEach(section => {
                            const el = document.querySelector(section.selector);
                            if (el && data.sections[section.id]) {
                                el.innerHTML = getSectionInnerHTML(data.sections[section.id], section.selector);
                            }
                        });
                    }

                    // Update cart count
                    const cartResponse = await fetch('/cart.js');
                    const cart = await cartResponse.json();
                    if (Alpine.store('xCartHelper')) {
                        Alpine.store('xCartHelper').currentItemCount = cart.item_count;
                    }

                    // Open cart drawer
                    if (Alpine.store('xMiniCart')) {
                        Alpine.store('xMiniCart').openCart();
                    }

                    document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
                }
            } catch (error) {
                console.error('Add to cart failed:', error);
            } finally {
                this.loading = false;
            }
        }
    }));

    /**
     * Collection Search Component Factory
     * Returns the Alpine.js component for AJAX search in collections
     * 
     * @param {string} sectionId - The section ID
     * @param {string} collectionUrl - The collection URL
     * @param {string} collectionId - The collection ID for filtering
     * @param {string} initialQuery - Initial search query from URL params
     */
    window.mlynCollectionSearchInit = function (sectionId, collectionUrl, collectionId, initialQuery) {
        return {
            searchQuery: initialQuery || '',
            isSearching: false,
            noResults: false,
            originalContent: null,
            abortController: null,

            init() {
                // Store original grid content for restoring
                const container = document.getElementById('product-grid-container');
                if (container) {
                    this.originalContent = container.innerHTML;
                    this.bindPagination(container);
                }
            },

            bindPagination(container) {
                container.addEventListener('click', (e) => {
                    const link = e.target.closest('a[data-ajax-page]');
                    if (!link) return;
                    e.preventDefault();
                    const url = link.getAttribute('href');
                    if (!url) return;
                    this.fetchPage(url);
                });
            },

            async fetchPage(url) {
                this.isSearching = true;
                this.noResults = false;
                try {
                    const response = await fetch(url, {
                        headers: { 'X-Requested-With': 'XMLHttpRequest' },
                    });
                    if (!response.ok) throw new Error('Search page fetch failed');
                    const html = await response.text();
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    const newGridContainer = doc.getElementById('product-grid-container');
                    const currentGridContainer = document.getElementById('product-grid-container');
                    if (newGridContainer && currentGridContainer) {
                        const productGrid = newGridContainer.querySelector('#product-grid');
                        if (productGrid && productGrid.children.length > 0) {
                            currentGridContainer.innerHTML = newGridContainer.innerHTML;
                            this.enforceCollectionFilter(currentGridContainer, collectionId);
                        } else {
                            currentGridContainer.style.display = 'none';
                            this.noResults = true;
                        }
                    }
                } catch (error) {
                    console.error('Search pagination error:', error);
                } finally {
                    this.isSearching = false;
                }
            },

            async performSearch() {
                const query = this.searchQuery.trim();

                // Cancel any pending requests
                if (this.abortController) {
                    this.abortController.abort();
                }

                // If search is empty, restore original content
                if (query === '') {
                    this.restoreOriginal();
                    return;
                }

                this.isSearching = true;
                this.noResults = false;
                this.abortController = new AbortController();

                try {
                    // Build search URL against /search with collection filter
                    const searchUrl = new URL('/search', window.location.origin);
                    searchUrl.searchParams.set('view', 'mlyn-collection-grid');
                    searchUrl.searchParams.set('type', 'product');
                    searchUrl.searchParams.set('filter.p.collection_id', collectionId);
                    searchUrl.searchParams.set('options[prefix]', 'last');
                    searchUrl.searchParams.set('collection_id', collectionId);
                    const baseQuery = query === '' ? '*' : query;
                    searchUrl.searchParams.set('q', baseQuery);

                    const response = await fetch(searchUrl.toString(), {
                        signal: this.abortController.signal,
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                    });

                    if (!response.ok) throw new Error('Search failed');

                    const html = await response.text();

                    // Parse the response and extract the product grid
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    const newGridContainer = doc.getElementById('product-grid-container');
                    const currentGridContainer = document.getElementById('product-grid-container');

                    if (newGridContainer && currentGridContainer) {
                        // Check if there are products
                        const productGrid = newGridContainer.querySelector('#product-grid');
                        if (productGrid && productGrid.children.length > 0) {
                            currentGridContainer.innerHTML = newGridContainer.innerHTML;
                            this.enforceCollectionFilter(currentGridContainer, collectionId);
                        } else {
                            // No products found
                            currentGridContainer.style.display = 'none';
                            this.noResults = true;
                        }
                    }
                } catch (error) {
                    if (error.name !== 'AbortError') {
                        console.error('Search error:', error);
                    }
                } finally {
                    this.isSearching = false;
                }
            },

            restoreOriginal() {
                const container = document.getElementById('product-grid-container');
                if (container && this.originalContent) {
                    container.innerHTML = this.originalContent;
                    container.style.display = 'block';
                }
                this.noResults = false;
            },

            clearSearch() {
                this.searchQuery = '';
                this.restoreOriginal();
            },

            enforceCollectionFilter(container, targetId) {
                // client-side enforcement: drop items not in this collection (fallback)
                const liNodes = container.querySelectorAll('#product-grid > li');
                const pageSize = Number(container.getAttribute('data-page-size')) || 50;
                let kept = 0;
                liNodes.forEach((li) => {
                    const cols = (li.getAttribute('data-collections') || '').split(',').filter(Boolean);
                    if (cols.length && !cols.includes(targetId)) {
                        li.remove();
                    } else {
                        kept += 1;
                    }
                });
                // hide pagination if fewer than a page of matches
                const pagination = container.querySelector('#pagination-container');
                if (pagination && kept < pageSize) {
                    pagination.remove();
                }

                if (kept === 0) {
                    container.style.display = 'none';
                    this.noResults = true;
                } else {
                    container.style.display = 'block';
                    this.noResults = false;
                }
            },
        };
    };

});
